/** Ask-about-this-call, remembered in this browser. The backend does not store it. */

export type AskTurn = { question: string; answer: string };

export type AskChat = { id: string; at: string; turns: AskTurn[] };

const HISTORY = (botId: string) => `dooglie.ask-call.history.${botId}`;
const SESSION = (botId: string) => `dooglie.ask-call.session.${botId}`;

export function newSession(): AskChat {
  return { id: crypto.randomUUID(), at: new Date().toISOString(), turns: [] };
}

function isTurn(value: unknown): value is AskTurn {
  if (!value || typeof value !== "object") return false;
  const turn = value as AskTurn;
  return typeof turn.question === "string" && typeof turn.answer === "string";
}

function isChat(value: unknown): value is AskChat {
  if (!value || typeof value !== "object") return false;
  const chat = value as AskChat;
  return (
    typeof chat.id === "string" &&
    typeof chat.at === "string" &&
    Array.isArray(chat.turns) &&
    chat.turns.every(isTurn)
  );
}

function read(key: string): unknown {
  try {
    const raw = localStorage.getItem(key) ?? sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function loadHistory(botId: string): AskChat[] {
  const parsed = read(HISTORY(botId));
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isChat);
}

export function loadSession(botId: string): AskChat | null {
  const parsed = read(SESSION(botId));
  return isChat(parsed) ? parsed : null;
}

export function saveSession(botId: string, session: AskChat): void {
  sessionStorage.setItem(SESSION(botId), JSON.stringify(session));
}

/** The visit left the call page: keep its questions as history, and end the live thread. */
export function archiveSession(botId: string, visitId: string): void {
  const session = loadSession(botId);
  if (!session || session.id !== visitId) return;
  sessionStorage.removeItem(SESSION(botId));
  if (session.turns.length === 0) return;
  const history = loadHistory(botId).filter((chat) => chat.id !== session.id);
  history.push(session);
  writeHistory(botId, history);
}

/** A follow-up asked while reading an earlier chat. */
export function replaceChat(botId: string, chat: AskChat): AskChat[] {
  const history = loadHistory(botId);
  const next = history.some((item) => item.id === chat.id)
    ? history.map((item) => (item.id === chat.id ? chat : item))
    : [...history, chat];
  writeHistory(botId, next);
  return next;
}

function writeHistory(botId: string, chats: AskChat[]): void {
  localStorage.setItem(HISTORY(botId), JSON.stringify(chats.slice(-40)));
}
